import { faPlus } from "@fortawesome/free-solid-svg-icons"
import {
  faChildren,
  faClock,
  faEnvelope,
  faPhone,
  faLocationDot,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  BanIcon,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Switch,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useState, useEffect } from "react"

import useLateCancelSessionsCount from "./hooks/useLateCancelSessionsCount"
import { useNoShowSessionsCount } from "./hooks/useNoShowSessionsCount"
import useInfoPanelStore from "./store/useInfoPanelStore"
import useIntakeQuestionnaireModalStore from "./store/useSurveysModalStore"
import { useSendbird } from "../../components/sendbird/SBProvider"
import useMediaQuery from "../../hooks/useMediaQuery"
import { useCoachingGroupsQuery } from "../../networking/coachingGroups/queries"
import { useCoachingSubscriptionQuery } from "../../networking/coachingGroups/queries"
import { useFamilyQuery } from "../../networking/families/queries"
import { useScheduledMessagesQuery } from "../../networking/scheduledMessages/queries"
import { useSessionBillingPeriodsQuery } from "../../networking/sessions/queries"
import { useToggleSMSMutation } from "../../networking/user/mutations"
import {
  useUserQuery,
  useUserSMSStatusQuery,
} from "../../networking/user/queries"
import { CoachingSubscriptionStatus } from "../../types"
import { ScheduledMessageWithRoutine } from "../../types/scheduledMessages"
import { ANALYTIC_EVENTS } from "../../util/analytics"
import { trackAnalyticEvent } from "../../util/analytics"
import { formatDate, getCurrentAge } from "../../util/dates"
import { getSortedChildrenFromFamily } from "../../util/profiles"
import {
  getFormattedTimeInTimezone,
  getRelativeTimeUntil,
  useLocalTime,
} from "../../util/time"
import useCurrentClient from "../clients/hooks/useCurrentClient"
import useScheduledMessageModalStore from "../clients/routines/useScheduledMessageModalStore"

const InfoPanel = () => {
  const isMobile = useMediaQuery("(max-width: 800px)")
  const { onOpen: openIntakeQuestionnaireModal } =
    useIntakeQuestionnaireModalStore()
  const { isOpen: isOpenInfoPanel } = useInfoPanelStore()
  const { isLoading: isLoadingCoachingGroups } = useCoachingGroupsQuery()
  const { currentClientProfile } = useCurrentClient()
  const { currentChannel } = useSendbird()
  const { data: family } = useFamilyQuery()

  const localTime = useLocalTime(
    currentClientProfile?.user.timezone as string,
    [currentClientProfile]
  )

  const children = getSortedChildrenFromFamily(family)

  if (!isOpenInfoPanel) return <></>
  if (!currentChannel) return <></>
  if (isMobile) return <></>
  if (isLoadingCoachingGroups) return <></>

  return (
    <FlexBox
      style={{
        overflow: "auto",
        height: "100%",
        width: "350px",
        minWidth: "300px",
        maxWidth: "min(400px, 100vw)",
        padding: SPACING.space6,
        background:
          "linear-gradient(0deg, rgba(233, 234, 250, 0.20) 0%, rgba(233, 234, 250, 0.20) 100%)",
        borderLeft: `1px solid ${JoonUIColor.border.default}`,
        boxShadow: "inset 5px 0px 15px -5px rgba(190, 195, 205, 0.5",
      }}
    >
      <FlexBox direction="column" wrap={false} gap={SPACING.space4}>
        <FlexBox direction="column" wrap={false} gap={SPACING.space2}>
          <Typography variant="bodyBold">
            {currentClientProfile?.user.name}{" "}
            {currentClientProfile?.user.last_name
              ? currentClientProfile?.user.last_name
              : ""}
          </Typography>

          <FlexBox direction="column" wrap={false} gap={SPACING.space2}>
            <div className="flex flex-row gap-2">
              <div style={{ minWidth: "14px" }}>
                <FontAwesomeIcon
                  icon={faChildren}
                  color={JoonUIColor.icon.accent}
                  fontSize={14}
                />
              </div>
              <Typography variant="bodySmall">
                {children.map((child, index) => (
                  <Typography variant="bodySmall" key={child.user.id}>
                    {child.user.name}
                    {child.user.birthdate &&
                      ` (${getCurrentAge(child.user.birthdate)})`}
                    {index !== children.length - 1 && ", "}
                  </Typography>
                ))}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <div style={{ minWidth: "14px" }}>
                <FontAwesomeIcon
                  icon={faPhone}
                  color={JoonUIColor.icon.accent}
                  fontSize={14}
                />
              </div>
              <Typography variant="bodySmall">
                {currentClientProfile?.user.phone_number || "None"}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <div style={{ minWidth: "14px" }}>
                <FontAwesomeIcon
                  icon={faEnvelope}
                  color={JoonUIColor.icon.accent}
                  fontSize={14}
                />
              </div>
              <Typography variant="bodySmall">
                {currentClientProfile?.user.email}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <div style={{ minWidth: "14px" }}>
                <FontAwesomeIcon
                  icon={faClock}
                  color={JoonUIColor.icon.accent}
                  fontSize={14}
                />
              </div>
              <Typography variant="bodySmall">
                It's <span style={{ fontWeight: 600 }}>{localTime}</span> for{" "}
                {currentClientProfile?.user.name}{" "}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <div style={{ minWidth: "14px" }}>
                <FontAwesomeIcon
                  icon={faLocationDot}
                  color={JoonUIColor.icon.accent}
                  fontSize={14}
                />
              </div>
              <Typography variant="bodySmall">
                Timezone:{" "}
                {currentClientProfile?.user?.timezone?.replace(/_/g, " ")}
              </Typography>
            </div>
          </FlexBox>
          <button onClick={openIntakeQuestionnaireModal}>
            <Typography
              variant="bodySmallBold"
              color={JoonUIColor.semantic.primary}
              textAlign="left"
            >
              View survey data
            </Typography>
          </button>
        </FlexBox>
        <UpcomingSessionSection />
        <PlanDetailsSection />
        <ScheduledMessageSection />
        <ToggleSMSSection />
      </FlexBox>
      <DevInfoSection />
    </FlexBox>
  )
}

export default InfoPanel

const ToggleSMSSection = () => {
  const { currentClientProfile } = useCurrentClient()

  const { data: smsIsEnabled } = useUserSMSStatusQuery()
  const { mutate: toggleSMS } = useToggleSMSMutation()

  const handleToggleSMS = () => {
    toggleSMS({
      userId: currentClientProfile?.user.id!,
      status: !smsIsEnabled,
    })
  }

  return (
    <div className="flex flex-col gap-2 border border-borderPrimary rounded-md p-4 bg-white w-full">
      <div className="flex flex-row items-center gap-2">
        <Switch
          name="sms-toggle"
          checked={smsIsEnabled}
          onChange={handleToggleSMS}
          variant="small"
        />
        <Typography variant="body">
          Texting is {smsIsEnabled ? "ON" : "OFF"}
        </Typography>
      </div>
      <Typography variant="bodySmall" color={JoonUIColor.text.secondary}>
        {smsIsEnabled
          ? "Your messages will be sent as texts that they can respond to directly."
          : "When enabled, your messages will be sent as texts that they can respond to directly."}
      </Typography>
    </div>
  )
}

const DevInfoSection = () => {
  const [showMoreInfo, setShowMoreInfo] = useState(false)
  const { data: user } = useUserQuery()
  const { currentChannel } = useSendbird()
  const { currentCoachingGroup } = useCurrentClient()

  // @ts-ignore - Not on user type yet
  if (!user?.enable_developer_mode) return <></>

  return (
    <FlexBox
      direction="column"
      gap={SPACING.space2}
      style={{ marginTop: "auto" }}
    >
      {showMoreInfo && (
        <FlexBox
          direction="column"
          wrap={false}
          style={{
            borderTop: `1px solid ${JoonUIColor.border.default}`,
            borderBottom: `1px solid ${JoonUIColor.border.default}`,
            padding: `${SPACING.space4} 0`,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="bodyBold" style={{ fontFamily: "monospace" }}>
            Admin info
          </Typography>
          <Typography variant="bodySmallBold">Coaching Group</Typography>
          <Typography variant="bodySmall">
            {currentCoachingGroup?.id}
          </Typography>
          <Typography variant="bodySmallBold">Members</Typography>
          {currentCoachingGroup?.profiles.map((profile) => (
            <FlexBox
              direction="column"
              wrap={false}
              style={{
                padding: SPACING.space2,
                borderRadius: SPACING.space2,
                background: JoonUIColor.background.lightGray,
                width: "100%",
                marginBottom: SPACING.space1,
              }}
            >
              <Typography variant="bodySmall" style={{ fontWeight: "600" }}>
                {profile.user.name} ({profile.role})
              </Typography>
              <Typography variant="bodyXSmall">{profile.user.email}</Typography>
              <Typography variant="bodyXSmall">
                User ID: {profile.user.id}
              </Typography>
              <Typography variant="bodyXSmall">
                Profile ID: {profile.id}
              </Typography>
            </FlexBox>
          ))}
          <Typography variant="bodySmallBold">SendbirdChannel URL</Typography>
          <Typography variant="bodyXSmall" style={{ wordBreak: "break-word" }}>
            {currentChannel?.url}
          </Typography>
          <Typography variant="bodySmallBold">
            Channel creation date:
          </Typography>
          <Typography variant="bodyXSmall">
            {/* @ts-ignore */}
            {new Date(currentChannel?._createdAt).toLocaleDateString()}
          </Typography>
        </FlexBox>
      )}
      <TextButton
        onClick={() => setShowMoreInfo(!showMoreInfo)}
        style={{ margin: "0 auto" }}
      >
        <Typography
          variant="bodySmallBold"
          color={JoonUIColor.text.primaryAccent}
        >
          {showMoreInfo ? "Hide" : "Show admin info"}
        </Typography>
      </TextButton>
    </FlexBox>
  )
}

const useUpcomingSession = () => {
  const { data: billingPeriods } = useSessionBillingPeriodsQuery()
  const upcomingSession = billingPeriods
    ?.flatMap((period) => period.bookings)
    ?.find(
      (booking) =>
        new Date(booking.start_datetime) > new Date() &&
        !booking.marked_late_cancellation &&
        !booking.marked_no_show
    )
  return upcomingSession
}

const UpcomingSessionSection = () => {
  const upcomingSession = useUpcomingSession()

  if (!upcomingSession) return <></>

  return (
    <FlexBox
      direction="column"
      gap={SPACING.space2}
      style={{
        marginBottom: "auto",
        borderRadius: SPACING.space2,
        border: `1px solid ${JoonUIColor.border.default}`,
        background: JoonUIColor.background.primaryNeutral,
        padding: SPACING.space4,
      }}
    >
      <Typography variant="bodyXSmallBold" color={JoonUIColor.text.secondary}>
        UPCOMING SESSION
      </Typography>
      <Typography variant="bodySmall">
        {formatDate(new Date(upcomingSession?.start_datetime))},{" "}
        {formatDate(new Date(upcomingSession?.start_datetime), "h:mm a", false)}
      </Typography>
    </FlexBox>
  )
}

const PlanDetailsSection = () => {
  const noShowCount = useNoShowSessionsCount()
  const lateCancelCount = useLateCancelSessionsCount()
  const { data: clientSubscriptionInfo } = useCoachingSubscriptionQuery()

  if (!clientSubscriptionInfo) return <></>

  return (
    <FlexBox
      direction="column"
      gap={SPACING.space2}
      style={{
        borderRadius: SPACING.space2,
        border: `1px solid ${JoonUIColor.border.default}`,
        background: JoonUIColor.background.primaryNeutral,
        padding: SPACING.space4,
      }}
    >
      <Typography variant="bodyXSmallBold" color={JoonUIColor.text.secondary}>
        COACHING PLAN
      </Typography>
      <div>
        <Typography variant="bodySmall" textAlign="left">
          {clientSubscriptionInfo.num_sessions} sessions / month
        </Typography>
        <Typography variant="bodySmall">
          {clientSubscriptionInfo.set_to_cancel
            ? `Expires ${formatDate(
                new Date(clientSubscriptionInfo.active_period_end_date),
                "MMM D"
              )}`
            : `Next cycle starts ${formatDate(
                new Date(clientSubscriptionInfo.active_period_end_date),
                "MMM D"
              )}`}
        </Typography>
        {clientSubscriptionInfo.status ===
          CoachingSubscriptionStatus.EXPIRED && (
          <Typography textAlign="left" variant="bodySmall">
            <Typography
              variant="bodySmallBold"
              style={{ color: JoonUIColor.semantic.alert }}
              textAlign="left"
            >
              Payment Failed
            </Typography>
            <Typography variant="bodySmall">
              Subscription will cancel if it continues to fail.
            </Typography>
          </Typography>
        )}
      </div>
      {(!!noShowCount || !!lateCancelCount) && (
        <FlexBox
          direction="row"
          gap={SPACING.space1}
          align="center"
          style={{
            background: JoonColorExpanded.red100,
            borderRadius: "100px",
            width: "fit-content",
            padding: `${SPACING.space05} ${SPACING.space2}`,
          }}
        >
          <BanIcon />
          {noShowCount > 0 && (
            <Typography variant="bodySmall">No-show: {noShowCount}</Typography>
          )}
          {noShowCount > 0 && lateCancelCount > 0 && " | "}
          {lateCancelCount > 0 && (
            <Typography variant="bodySmall">
              Late cancel: {lateCancelCount}
            </Typography>
          )}
        </FlexBox>
      )}
    </FlexBox>
  )
}

const ScheduledMessageSection = () => {
  const { data: scheduledMessages } = useScheduledMessagesQuery()
  const { onOpen: openScheduledMessageModal } = useScheduledMessageModalStore()
  const [currentTime, setCurrentTime] = useState(dayjs())
  const { currentClientProfile } = useCurrentClient()

  // Update the current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(dayjs())
    }, 60000)

    return () => clearInterval(interval)
  }, [])

  const messagesInFuture = scheduledMessages?.filter((message) =>
    dayjs(message.datetime).isAfter(currentTime)
  )

  const handleAddScheduledMessage = () => {
    openScheduledMessageModal()
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_ADD_SCHEDULED_MESSAGE, {
      source: "info_panel",
    })
  }

  const handleViewScheduledMessage = (message: ScheduledMessageWithRoutine) => {
    openScheduledMessageModal({
      scheduledMessage: message,
      timezone: currentClientProfile?.user.timezone!,
    })
  }

  return (
    <div className="flex flex-col gap-1 border border-borderPrimary rounded-md p-4 bg-white w-full">
      <div className="flex flex-row justify-between items-center">
        <Typography variant="bodyXSmallBold" color={JoonUIColor.text.secondary}>
          SCHEDULED MESSAGES
        </Typography>
        <TextButton
          style={{ padding: SPACING.space1, borderRadius: SPACING.space1 }}
          onClick={handleAddScheduledMessage}
        >
          <FontAwesomeIcon icon={faPlus} color={JoonUIColor.icon.neutral} />
        </TextButton>
      </div>
      <div className="flex flex-col">
        {messagesInFuture?.map((message, index) => {
          const clientTime = getFormattedTimeInTimezone(
            message.datetime,
            currentClientProfile?.user.timezone!
          )
          return (
            <button
              onClick={() => handleViewScheduledMessage(message)}
              key={message.id}
            >
              <div
                className={`flex flex-col hover:bg-bgXLightGray py-2 border-borderPrimary ${
                  index === messagesInFuture.length - 1
                    ? "border-b-0"
                    : "border-b"
                }`}
              >
                <Typography
                  variant="bodyXSmall"
                  textAlign="left"
                  color={JoonUIColor.text.secondary}
                >
                  {getRelativeTimeUntil(message.datetime, true)} ({clientTime}{" "}
                  for {currentClientProfile?.user.name})
                </Typography>
                <Typography
                  variant="bodySmall"
                  textAlign="left"
                  className="whitespace-nowrap overflow-hidden text-ellipsis w-full"
                >
                  {message.message}
                </Typography>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}
